import {
  ArrowPathIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/20/solid";
import type {PropsWithChildren, ReactNode, Ref} from "react";
import {AdminPage} from "../components/AdminPage";
import {getOtsActionButtonClassName, OtsActionButton, OtsIconActionButton} from "../components/OtsButton";
import {OtsDayStepper} from "../components/OtsDayStepper";
import {OtsPopover} from "../components/OtsPopover";
import {OtsThemeRoot} from "../theme/OtsTheme";

export type TicketWorkspaceProps = PropsWithChildren<{
  date: Date;
  onDateChange: (date: Date) => void;
  ticketCount: number;
  showSearch: boolean;
  onSearchClick: () => void;
  filterActive: boolean;
  onFilterClick: () => void;
  isRefreshing?: boolean;
  onRefresh: () => void;
  onNewTicket: () => void;
  onCompare: () => void;
  onReports: () => void;
  /** Temporary page context stays outside the grid sizing and scrolling surface. */
  context?: ReactNode;
  /** The hosted grid portals its own column and separator controls here. */
  gridControlsRef?: Ref<HTMLDivElement>;
}>;

/** Compact Tickets shell: one wrapping header, with all remaining height reserved for the grid. */
export const TicketWorkspace = ({
  date,
  onDateChange,
  ticketCount,
  showSearch,
  onSearchClick,
  filterActive,
  onFilterClick,
  isRefreshing,
  onRefresh,
  onNewTicket,
  onCompare,
  onReports,
  gridControlsRef,
  context,
  children,
}: TicketWorkspaceProps) => (
  <OtsThemeRoot className="h-full min-h-0 min-w-0">
    <AdminPage
      title="Tickets"
      status={
        <span aria-label="Ticket count" className="text-xs tabular-nums text-[var(--ots-text-muted)]">
          {ticketCount.toLocaleString()}
        </span>
      }
      fullWidth
      layout="fill"
      density="compact"
      headerLayout="workspace"
      headerControlsPlacement="center"
      headerControls={
        <div role="group" aria-label="Ticket date navigation">
          <OtsDayStepper size="compact" value={date} onChange={onDateChange} />
        </div>
      }
      actions={
        <>
          <div role="group" aria-label="Find tickets" className="flex items-center gap-1">
            <OtsIconActionButton
              size="compact"
              variant={showSearch ? "tonal" : "text"}
              icon={<MagnifyingGlassIcon />}
              label="Search tickets"
              aria-pressed={showSearch}
              onClick={onSearchClick}
            />
            <OtsIconActionButton
              size="compact"
              variant={filterActive ? "tonal" : "text"}
              icon={<FunnelIcon />}
              label={filterActive ? "Filter tickets (active)" : "Filter tickets"}
              aria-pressed={filterActive}
              onClick={onFilterClick}
            />
          </div>
          <span aria-hidden="true" className="h-5 w-px shrink-0 bg-[var(--ots-outline-variant)]" />
          <div ref={gridControlsRef} role="group" aria-label="Ticket grid controls" />
          <span aria-hidden="true" className="h-5 w-px shrink-0 bg-[var(--ots-outline-variant)]" />
          <div role="group" aria-label="Ticket actions" className="flex items-center gap-1">
            <OtsIconActionButton
              size="compact"
              variant="text"
              icon={<ArrowPathIcon className={isRefreshing ? "animate-spin" : undefined} />}
              label="Refresh tickets"
              disabled={isRefreshing}
              onClick={onRefresh}
            />
            <OtsIconActionButton
              size="compact"
              variant="text"
              icon={<PlusIcon />}
              label="New ticket"
              onClick={onNewTicket}
            />
            <OtsPopover
              density="compact"
              placement="bottom end"
              ariaLabel="More ticket actions"
              tooltip="More ticket actions"
              trigger={<EllipsisHorizontalIcon aria-hidden="true" className="size-[18px]" />}
              buttonClassName={getOtsActionButtonClassName("text", "compact", "!size-8 !p-0")}
              panelClassName="flex min-w-44 flex-col gap-1"
            >
              {({close}) => (
                <>
                  <OtsActionButton
                    size="compact"
                    variant="text"
                    icon={<DocumentDuplicateIcon />}
                    className="w-full justify-start whitespace-nowrap"
                    onClick={() => {
                      close();
                      onCompare();
                    }}
                  >
                    Compare tickets
                  </OtsActionButton>
                  <OtsActionButton
                    size="compact"
                    variant="text"
                    icon={<ChartBarIcon />}
                    className="w-full justify-start whitespace-nowrap"
                    onClick={() => {
                      close();
                      onReports();
                    }}
                  >
                    Reports
                  </OtsActionButton>
                </>
              )}
            </OtsPopover>
          </div>
        </>
      }
    >
      {context != null && <div className="mt-2 shrink-0">{context}</div>}
      {/* Legacy hosts reset section display; keep the grid on a definite flex sizing chain. */}
      <section
        aria-label="Tickets data grid"
        aria-busy={isRefreshing || undefined}
        className="relative mt-2 !flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]"
      >
        {children}
      </section>
    </AdminPage>
  </OtsThemeRoot>
);
